/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppButton, getSecondaryButtonOverrideStyles } from '../../components';
import { Label, Link, Dialog, DialogFooter, Spinner, SpinnerType } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { getRecoveringPageStyles, getSpinnerStyles, getRecoveryLinkStyles } from '../../common/pageStyles/RecoveringPage.styles';
import { TranscriptionRoutePathNames } from '../../routes';
import { AppLayoutSettings } from '../../common/styles/common.styles';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { CancelRecoveryTask, GetAudioBackupRecoveryTaskResultStatus, RecoverFromBackup, SetRecoveredTranscriptionFile, SetRecoveryFlowCancelDialog } from '../../common/actions/RecoveringActions';
import { SetFailureStateAndRouteToFailure } from '../../actions/FailureActions';
import { RecoveryKeys } from '../../common/telemetry/RecoveryKeys';
import { ErrorStringFromException } from '../../../utils/utils';
import { AppAuthenticationProviderContext } from '../../context';
import { ITranscriptionSourceFile } from '../../../backend/interfaces/ITranscriptionSourceFileProvider';
import { HostAppSettings, HostAppSettingName } from '../../../backend/office-js/HostAppSettings';
import { IRealTimeTranscriptionConnection } from '../../../backend/interfaces/IRealTimeTranscriptionConnection';
import { CreateDefaultRealTimeTranscriptionConnection } from '../../../backend/factory/RealTimeTranscriptionConnectionFactory';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import recoveringStore from '../../stores/RecoveringStore';
import { ChangeEvent } from '../../stores/utils';
import { AuthPopup } from '../../components/Popups/AuthPopup';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { TranscriptionTelemetryErrorCodes } from '../../../utils/TranscriptionTelemetryErrorCodes';
import { ITranscriptionErrorEvent } from '../../../backend/interfaces/ITranscriptionErrorEvent';
import { getDialogContentProps, getDialogStyles, getModalProps, getTopLabelStyles } from '../../common/styles/Popup.styles';
import { TaskListResultStatus } from '../../../backend/transcriptionManager/TaskList';
import { FailureContinuation } from '../../common/enums/failureEnums';

export interface RecoveringState {
  recoveredTranscriptionFile: ITranscriptionSourceFile | null;
  shouldShowRecoveryCancelDialog: boolean;
}

export class Recovering extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>, RecoveringState> {
  static contextType = AppAuthenticationProviderContext;
  private _isMounted: boolean;

  constructor(props: RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>) {
    super(props);
    this._isMounted = false;

    this.state = recoveringStore.getState();
    recoveringStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async componentWillMount() {
    await this._performRecoveryTasks();
  }

  public componentWillUnmount() {
    this._isMounted = false;
  }

  public async componentDidMount() {
    this._isMounted = true;
  }

  public render() {
    const recoveryPageUI = this.state.recoveredTranscriptionFile ? this._getRecoveryCompletedUI() : this._getRecoveryProgressUI();

    return (
      <div>
        {recoveryPageUI}
        <AuthPopup />
        {!this.state.recoveredTranscriptionFile && this.state.shouldShowRecoveryCancelDialog ? this.showRecoveryFlowDialog() : null}
      </div>
    );
  }

  private _getRecoveryCompletedUI() {
    const styles = getRecoveringPageStyles();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div className={styles.labelContainer}>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.FileRecoveryCompletionMessage}</Label>
          <Link styles={getRecoveryLinkStyles()} href={this.state.recoveredTranscriptionFile!.permanentAudioUrl} target="_blank">{this.state.recoveredTranscriptionFile!.fileName}</Link>
        </div>
        <div className={styles.buttonContainer}>
          <AppButton type={'AppButtonProps'} id="GotItButton" text={TranscriptionStrings.FileRecoveryGotItButtonLabel} onExecute={() => { this._navigateAway("got it clicked") }} />
        </div>
      </div>
    );
  }

  private _getRecoveryProgressUI() {
    const styles = getRecoveringPageStyles();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.FileRecoveryUploadingLabel}</Label>
          <div className={styles.cancelLinkContainer}>
            <Link role="button" id='recoveringCancelButtonId' styles={getRecoveryLinkStyles()} onClick={() => { this._onCancelClicked() }} href="#">{TranscriptionStrings.LoadingPageCancelButtonLabel}</Link>
          </div>
        </div>
        <div className={styles.spinnerContainer}>
          <Spinner type={SpinnerType.large} label={TranscriptionStrings.FileRecoveryUploadingStatus} styles={getSpinnerStyles()} />
        </div>
      </div>
    );
  }

  private _onCancelClicked() {
    SetRecoveryFlowCancelDialog(true);
  }

  private showRecoveryFlowDialog() {
    const recoveryFlowDialogTitle: JSX.Element =
    <Label styles={getTopLabelStyles()}>
    {TranscriptionStrings.RecoveryFlowDialogTitle}
    </Label>

    return <Dialog
      dialogContentProps={getDialogContentProps(recoveryFlowDialogTitle)}
      modalProps={getModalProps()}
      hidden={!this.state.shouldShowRecoveryCancelDialog}
      styles={getDialogStyles()}>
      <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.RecoveryFlowDialogDescription}</Label>
      <DialogFooter>
        <AppButton
          type={'AppButtonProps'}
          id="RecoveryFlowNoButtonID"
          styles={getSecondaryButtonOverrideStyles()}
          text={TranscriptionStrings.RecoveryFlowDialogNoButtonLabelText}
          onExecute={this._onNoClicked}
        />
        <AppButton
          type={'AppButtonProps'}
          id="RecoveryFlowYesButtonID"
          text={TranscriptionStrings.RecoveryFlowDialogYesButtonLabelText}
          onExecute={this._onYesClicked}
        />
      </DialogFooter>
    </Dialog>
  }

  private _onNoClicked() {
    SetRecoveryFlowCancelDialog(false);
  }

  private _onYesClicked = async () => {
    // cancel ongoing recovery task
    await CancelRecoveryTask();
    if (this.props.location.state.audioBackup != null) {
      await this.props.location.state.audioBackup.ClearBackup();
      await this.props.location.state.audioBackup.Initialize();
    }
    const realTimeTranscriptionConnection: IRealTimeTranscriptionConnection = CreateDefaultRealTimeTranscriptionConnection();
    this.props.history.replace(TranscriptionRoutePathNames.Landing, { realTimeTranscriptionConnection, audioBackup: this.props.location.state.audioBackup});
    SetRecoveryFlowCancelDialog(false);
    const reason: string = "operation was canceled by user";
    LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: reason}]);
  }

  private async _performRecoveryTasks() {
    try {
      LogEvents.Recovery.Start();
      let recoveredFile: ITranscriptionSourceFile | null = await RecoverFromBackup(this.props.location.state.audioBackup!, this.context);

      // Update recovery page state, if file recovered successfully.
      if (recoveredFile != null) {
        LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: true}]);
        SetRecoveredTranscriptionFile(recoveredFile);
      } else {
        const uploadResultStatus: TaskListResultStatus = GetAudioBackupRecoveryTaskResultStatus();
        // if operation is not cancelled, we will navigate to loading or landing depending on condition
        if (uploadResultStatus != TaskListResultStatus.Canceled) {
          const reason: string = "operation was canceled";
          LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: reason}]);
          this._navigateAway(reason);
        }
      }
    } catch (e) {
      LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: ErrorStringFromException(e)}]);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      const transcriptionErrorEvent : ITranscriptionErrorEvent = {errorEventMessage: TranscriptionStrings.FileRecoveryFailedFailureReason , errorEventName : TranscriptionTelemetryErrorCodes.FileRecoveryFailedFailureReason};
      // Show Failure page
      SetFailureStateAndRouteToFailure(LogEventNames.Recovery, transcriptionErrorEvent, FailureContinuation.None /*failureContinuation*/, this.props.history);
    }
  }

  private _navigateAway(reason: string): void {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery - " + reason }]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    } else {
      // We want to create a transcription connection here to avoid paying the initialization price later, so it improves the "time to input" metric.
      const realTimeTranscriptionConnection: IRealTimeTranscriptionConnection = CreateDefaultRealTimeTranscriptionConnection();
      this.props.history.replace(TranscriptionRoutePathNames.Landing, { realTimeTranscriptionConnection, audioBackup: this.props.location.state.audioBackup});
    }
  };

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(recoveringStore.getState());
    }
  }
};